<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <h5 class="modal-title">
              <i class="fas fa-file-alt me-2"></i>
              Audit Details
            </h5>
            <small class="text-muted">{{ result.page_title }}</small>
          </div>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>

        <div class="modal-body">
          <!-- Page Info -->
          <div class="card mb-3">
            <div class="card-body">
              <div class="row">
                <div class="col-md-8">
                  <h6 class="card-subtitle mb-2 text-muted">Page URL</h6>
                  <a :href="result.page_url" target="_blank" class="text-break">
                    {{ result.page_url }}
                    <i class="fas fa-external-link-alt ms-1 small"></i>
                  </a>
                </div>
                <div class="col-md-4 text-end">
                  <div class="d-flex justify-content-end gap-2">
                    <span class="badge bg-danger fs-6">
                      {{ result.error_count }} Errors
                    </span>
                    <span class="badge bg-warning fs-6">
                      {{ result.warning_count }} Warnings
                    </span>
                    <span class="badge bg-info fs-6">
                      {{ result.notice_count }} Notices
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Remediation Progress -->
          <div v-if="remediationProgress && remediationProgress.total > 0" class="mb-3 d-flex align-items-center gap-2">
            <small class="text-muted">Remediation:</small>
            <div class="progress flex-grow-1" style="height:8px;">
              <div class="progress-bar bg-success" :style="{ width: remediationProgress.percentage + '%' }"></div>
            </div>
            <small class="text-muted">{{ remediationProgress.reviewed }}/{{ remediationProgress.total }} reviewed</small>
          </div>

          <!-- Tabs for IBM and WAVE results -->
          <ul class="nav nav-tabs mb-3" role="tablist">
            <li class="nav-item" role="presentation">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'ibm-violations' }"
                @click="activeTab = 'ibm-violations'"
                type="button"
              >
                <i class="fas fa-robot text-danger me-1"></i>
                IBM Violations ({{ ibmViolations.length }})
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'ibm-recommendations' }"
                @click="activeTab = 'ibm-recommendations'"
                type="button"
              >
                <i class="fas fa-robot text-warning me-1"></i>
                IBM Recommendations ({{ ibmRecommendations.length }})
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'wave-errors' }"
                @click="activeTab = 'wave-errors'"
                type="button"
              >
                <i class="fas fa-wave-square text-danger me-1"></i>
                WAVE Errors ({{ waveErrors.length }})
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'wave-alerts' }"
                @click="activeTab = 'wave-alerts'"
                type="button"
              >
                <i class="fas fa-wave-square text-warning me-1"></i>
                WAVE Alerts ({{ waveAlerts.length }})
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'wave-features' }"
                @click="activeTab = 'wave-features'"
                type="button"
              >
                <i class="fas fa-wave-square text-info me-1"></i>
                WAVE Features ({{ waveFeatures.length }})
              </button>
            </li>
          </ul>

          <!-- Tab Content -->
          <div class="tab-content">
            <!-- IBM Violations Tab -->
            <div v-if="activeTab === 'ibm-violations'">
              <div v-if="ibmViolations.length > 0">
                <!-- Design/Content Issues (for PMs/Designers) -->
                <div v-if="designContentViolations.length > 0" class="mb-4">
                  <h6 class="text-muted mb-2">
                    <i class="fas fa-palette me-1"></i>
                    Design / Content Issues
                    <span class="badge bg-warning text-dark ms-1">{{ designContentViolations.length }}</span>
                    <small class="fw-normal ms-2">For PMs / Designers</small>
                  </h6>
                  <div class="list-group">
                    <div
                      v-for="group in designContentViolations"
                      :key="'design-' + group.ruleId"
                      class="list-group-item"
                    >
                      <div class="d-flex w-100 justify-content-between">
                        <h6 class="mb-1">{{ group.ruleId }}</h6>
                        <span class="badge bg-danger" v-if="group.count > 1">x{{ group.count }}</span>
                      </div>
                      <p class="mb-1">{{ group.message || group.help || 'No description' }}</p>
                      <div v-if="group.count > 1">
                        <a href="#" class="small" @click.prevent="toggleExpanded(group)">
                          {{ isExpanded(group) ? 'Hide' : 'Show' }} {{ group.count }} locations
                        </a>
                        <div v-if="isExpanded(group)" class="mt-1">
                          <small v-for="(inst, i) in group.instances" :key="i" class="text-muted d-block">
                            {{ inst.xpath }}
                          </small>
                        </div>
                      </div>
                      <small class="text-muted" v-else-if="group.instances[0] && group.instances[0].xpath">
                        Location: {{ group.instances[0].xpath }}
                      </small>
                      <!-- Remediation controls -->
                      <div class="mt-2 pt-2 border-top" v-if="getRemediationEntry('ibm', group.ruleId, group.instances[0] && group.instances[0].xpath || '')">
                        <div class="d-flex align-items-center gap-2">
                          <select
                            class="form-select form-select-sm"
                            style="width:140px;"
                            :value="getRemediationEntry('ibm', group.ruleId, group.instances[0] && group.instances[0].xpath || '').status"
                            @change="updateRemediation(getRemediationEntry('ibm', group.ruleId, group.instances[0] && group.instances[0].xpath || ''), 'status', $event.target.value)"
                          >
                            <option value="open">Open</option>
                            <option value="fixed">Fixed</option>
                            <option value="acknowledged">Acknowledged</option>
                            <option value="wont_fix">Won't Fix</option>
                          </select>
                          <input
                            type="text"
                            class="form-control form-control-sm"
                            placeholder="Add comment..."
                            :value="getRemediationEntry('ibm', group.ruleId, group.instances[0] && group.instances[0].xpath || '').comment || ''"
                            @blur="updateRemediation(getRemediationEntry('ibm', group.ruleId, group.instances[0] && group.instances[0].xpath || ''), 'comment', $event.target.value)"
                            @keyup.enter="updateRemediation(getRemediationEntry('ibm', group.ruleId, group.instances[0] && group.instances[0].xpath || ''), 'comment', $event.target.value)"
                          />
                        </div>
                        <small v-if="getRemediationEntry('ibm', group.ruleId, group.instances[0] && group.instances[0].xpath || '').suggested_fix" class="text-info d-block mt-1">
                          <i class="fas fa-lightbulb me-1"></i>
                          Suggested: {{ getRemediationEntry('ibm', group.ruleId, group.instances[0] && group.instances[0].xpath || '').suggested_fix }}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Code/Structure Issues (for Developers) -->
                <div v-if="codeStructureViolations.length > 0">
                  <h6 class="text-muted mb-2">
                    <i class="fas fa-code me-1"></i>
                    Code / Structure Issues
                    <span class="badge bg-danger ms-1">{{ codeStructureViolations.length }}</span>
                    <small class="fw-normal ms-2">For Developers</small>
                  </h6>
                  <div class="list-group">
                    <div
                      v-for="group in codeStructureViolations"
                      :key="'code-' + group.ruleId"
                      class="list-group-item"
                    >
                      <div class="d-flex w-100 justify-content-between">
                        <h6 class="mb-1">{{ group.ruleId }}</h6>
                        <span class="badge bg-danger" v-if="group.count > 1">x{{ group.count }}</span>
                      </div>
                      <p class="mb-1">{{ group.message || group.help || 'No description' }}</p>
                      <div v-if="group.count > 1">
                        <a href="#" class="small" @click.prevent="toggleExpanded(group)">
                          {{ isExpanded(group) ? 'Hide' : 'Show' }} {{ group.count }} locations
                        </a>
                        <div v-if="isExpanded(group)" class="mt-1">
                          <small v-for="(inst, i) in group.instances" :key="i" class="text-muted d-block">
                            {{ inst.xpath }}
                          </small>
                        </div>
                      </div>
                      <small class="text-muted" v-else-if="group.instances[0] && group.instances[0].xpath">
                        Location: {{ group.instances[0].xpath }}
                      </small>
                      <!-- Remediation controls -->
                      <div class="mt-2 pt-2 border-top" v-if="getRemediationEntry('ibm', group.ruleId, group.instances[0] && group.instances[0].xpath || '')">
                        <div class="d-flex align-items-center gap-2">
                          <select
                            class="form-select form-select-sm"
                            style="width:140px;"
                            :value="getRemediationEntry('ibm', group.ruleId, group.instances[0] && group.instances[0].xpath || '').status"
                            @change="updateRemediation(getRemediationEntry('ibm', group.ruleId, group.instances[0] && group.instances[0].xpath || ''), 'status', $event.target.value)"
                          >
                            <option value="open">Open</option>
                            <option value="fixed">Fixed</option>
                            <option value="acknowledged">Acknowledged</option>
                            <option value="wont_fix">Won't Fix</option>
                          </select>
                          <input
                            type="text"
                            class="form-control form-control-sm"
                            placeholder="Add comment..."
                            :value="getRemediationEntry('ibm', group.ruleId, group.instances[0] && group.instances[0].xpath || '').comment || ''"
                            @blur="updateRemediation(getRemediationEntry('ibm', group.ruleId, group.instances[0] && group.instances[0].xpath || ''), 'comment', $event.target.value)"
                            @keyup.enter="updateRemediation(getRemediationEntry('ibm', group.ruleId, group.instances[0] && group.instances[0].xpath || ''), 'comment', $event.target.value)"
                          />
                        </div>
                        <small v-if="getRemediationEntry('ibm', group.ruleId, group.instances[0] && group.instances[0].xpath || '').suggested_fix" class="text-info d-block mt-1">
                          <i class="fas fa-lightbulb me-1"></i>
                          Suggested: {{ getRemediationEntry('ibm', group.ruleId, group.instances[0] && group.instances[0].xpath || '').suggested_fix }}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="alert alert-success">
                <i class="fas fa-check-circle me-2"></i>
                No IBM violations found on this page!
              </div>
            </div>

            <!-- IBM Recommendations Tab -->
            <div v-if="activeTab === 'ibm-recommendations'">
              <div v-if="ibmRecommendations.length > 0">
                <div class="list-group">
                  <div
                    v-for="(rec, index) in ibmRecommendations"
                    :key="'ibm-rec-' + index"
                    class="list-group-item"
                  >
                    <div class="d-flex w-100 justify-content-between">
                      <h6 class="mb-1">{{ rec.ruleId || 'Recommendation' }}</h6>
                      <small class="text-warning">{{ rec.level || 'Warning' }}</small>
                    </div>
                    <p class="mb-1">{{ rec.message || rec.help || 'No description available' }}</p>
                    <small class="text-muted" v-if="rec.xpath">Location: {{ rec.xpath }}</small>
                    <!-- Remediation -->
                    <div class="mt-2 pt-2 border-top" v-if="getRemediationEntry('ibm', rec.ruleId, rec.xpath || '')">
                      <div class="d-flex align-items-center gap-2">
                        <select class="form-select form-select-sm" style="width:140px;"
                          :value="getRemediationEntry('ibm', rec.ruleId, rec.xpath || '').status"
                          @change="updateRemediation(getRemediationEntry('ibm', rec.ruleId, rec.xpath || ''), 'status', $event.target.value)">
                          <option value="open">Open</option>
                          <option value="fixed">Fixed</option>
                          <option value="acknowledged">Acknowledged</option>
                          <option value="wont_fix">Won't Fix</option>
                        </select>
                        <input type="text" class="form-control form-control-sm" placeholder="Add comment..."
                          :value="getRemediationEntry('ibm', rec.ruleId, rec.xpath || '').comment || ''"
                          @blur="updateRemediation(getRemediationEntry('ibm', rec.ruleId, rec.xpath || ''), 'comment', $event.target.value)"
                          @keyup.enter="$event.target.blur()" />
                      </div>
                      <small v-if="getRemediationEntry('ibm', rec.ruleId, rec.xpath || '').suggested_fix" class="text-info d-block mt-1">
                        <i class="fas fa-lightbulb me-1"></i> Suggested: {{ getRemediationEntry('ibm', rec.ruleId, rec.xpath || '').suggested_fix }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="alert alert-success">
                <i class="fas fa-check-circle me-2"></i>
                No IBM recommendations for this page!
              </div>
            </div>

            <!-- WAVE Errors Tab -->
            <div v-if="activeTab === 'wave-errors'">
              <div v-if="waveErrors.length > 0">
                <div class="list-group">
                  <div v-for="(item, index) in waveErrors" :key="'wave-error-' + index" class="list-group-item">
                    <div class="d-flex w-100 justify-content-between">
                      <h6 class="mb-1">{{ item.id || 'Error' }}</h6>
                      <span class="badge bg-danger">{{ item.count || 1 }}</span>
                    </div>
                    <p class="mb-1">{{ item.description || 'No description available' }}</p>
                    <div v-if="getValidSelectors(item).length" class="mt-1">
                      <div v-for="(sel, si) in getValidSelectors(item).slice(0, 3)" :key="si" class="small text-muted mb-1">
                        <i class="fas fa-crosshairs me-1"></i>
                        <code>{{ simplifySelector(sel) }}</code>
                      </div>
                      <small v-if="getValidSelectors(item).length > 3" class="text-muted">
                        +{{ getValidSelectors(item).length - 3 }} more elements
                      </small>
                    </div>
                    <small v-else class="text-muted"><i class="fas fa-globe me-1"></i>Page-level issue</small>
                    <!-- Remediation -->
                    <div class="mt-2 pt-2 border-top" v-if="getRemediationEntry('wave', item.id, getValidSelectors(item)[0] || '')">
                      <div class="d-flex align-items-center gap-2">
                        <select class="form-select form-select-sm" style="width:140px;"
                          :value="getRemediationEntry('wave', item.id, getValidSelectors(item)[0] || '').status"
                          @change="updateRemediation(getRemediationEntry('wave', item.id, getValidSelectors(item)[0] || ''), 'status', $event.target.value)">
                          <option value="open">Open</option>
                          <option value="fixed">Fixed</option>
                          <option value="acknowledged">Acknowledged</option>
                          <option value="wont_fix">Won't Fix</option>
                        </select>
                        <input type="text" class="form-control form-control-sm" placeholder="Add comment..."
                          :value="getRemediationEntry('wave', item.id, getValidSelectors(item)[0] || '').comment || ''"
                          @blur="updateRemediation(getRemediationEntry('wave', item.id, getValidSelectors(item)[0] || ''), 'comment', $event.target.value)"
                          @keyup.enter="$event.target.blur()" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="alert alert-success">
                <i class="fas fa-check-circle me-2"></i>
                No WAVE errors found on this page!
              </div>
            </div>

            <!-- WAVE Alerts Tab -->
            <div v-if="activeTab === 'wave-alerts'">
              <div v-if="waveAlerts.length > 0">
                <div class="list-group">
                  <div v-for="(item, index) in waveAlerts" :key="'wave-alert-' + index" class="list-group-item">
                    <div class="d-flex w-100 justify-content-between">
                      <h6 class="mb-1">{{ item.id || 'Alert' }}</h6>
                      <span class="badge bg-warning text-dark">{{ item.count || 1 }}</span>
                    </div>
                    <p class="mb-1">{{ item.description || 'No description available' }}</p>
                    <div v-if="getValidSelectors(item).length" class="mt-1">
                      <div v-for="(sel, si) in getValidSelectors(item).slice(0, 3)" :key="si" class="small text-muted mb-1">
                        <i class="fas fa-crosshairs me-1"></i>
                        <code>{{ simplifySelector(sel) }}</code>
                      </div>
                      <small v-if="getValidSelectors(item).length > 3" class="text-muted">
                        +{{ getValidSelectors(item).length - 3 }} more elements
                      </small>
                    </div>
                    <small v-else class="text-muted"><i class="fas fa-globe me-1"></i>Page-level issue</small>
                    <!-- Remediation -->
                    <div class="mt-2 pt-2 border-top" v-if="getRemediationEntry('wave', item.id, getValidSelectors(item)[0] || '')">
                      <div class="d-flex align-items-center gap-2">
                        <select class="form-select form-select-sm" style="width:140px;"
                          :value="getRemediationEntry('wave', item.id, getValidSelectors(item)[0] || '').status"
                          @change="updateRemediation(getRemediationEntry('wave', item.id, getValidSelectors(item)[0] || ''), 'status', $event.target.value)">
                          <option value="open">Open</option>
                          <option value="fixed">Fixed</option>
                          <option value="acknowledged">Acknowledged</option>
                          <option value="wont_fix">Won't Fix</option>
                        </select>
                        <input type="text" class="form-control form-control-sm" placeholder="Add comment..."
                          :value="getRemediationEntry('wave', item.id, getValidSelectors(item)[0] || '').comment || ''"
                          @blur="updateRemediation(getRemediationEntry('wave', item.id, getValidSelectors(item)[0] || ''), 'comment', $event.target.value)"
                          @keyup.enter="$event.target.blur()" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="alert alert-success">
                <i class="fas fa-check-circle me-2"></i>
                No WAVE alerts found on this page!
              </div>
            </div>

            <!-- WAVE Features Tab -->
            <div v-if="activeTab === 'wave-features'">
              <div v-if="waveFeatures.length > 0">
                <div class="list-group">
                  <div v-for="(item, index) in waveFeatures" :key="'wave-feature-' + index" class="list-group-item">
                    <div class="d-flex w-100 justify-content-between">
                      <h6 class="mb-1">{{ item.id || 'Feature' }}</h6>
                      <span class="badge bg-info">{{ item.count || 1 }}</span>
                    </div>
                    <p class="mb-1">{{ item.description || 'Accessibility feature detected' }}</p>
                    <div v-if="getValidSelectors(item).length" class="mt-1">
                      <div v-for="(sel, si) in getValidSelectors(item).slice(0, 3)" :key="si" class="small text-muted mb-1">
                        <i class="fas fa-crosshairs me-1"></i>
                        <code>{{ simplifySelector(sel) }}</code>
                      </div>
                      <small v-if="getValidSelectors(item).length > 3" class="text-muted">
                        +{{ getValidSelectors(item).length - 3 }} more elements
                      </small>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="alert alert-info">
                <i class="fas fa-info-circle me-2"></i>
                No notable WAVE features detected on this page.
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-primary me-2"
            @click="openShowcase"
            :disabled="loadingShowcase"
            title="View issues overlaid on the actual webpage. If audit just completed, wait a moment to avoid rate limiting."
          >
            <i v-if="loadingShowcase" class="fas fa-spinner fa-spin me-1"></i>
            <i v-else class="fas fa-eye me-1"></i>
            {{ loadingShowcase ? 'Preparing...' : 'Live Showcase' }}
          </button>
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'

export default {
  name: 'DetailsModal',
  props: {
    result: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      activeTab: 'errors',
      loadingShowcase: false,
      expandedGroups: {},
      remediationLog: {},
      remediationProgress: null,
      savingRemediation: {},
    }
  },


  computed: {
    ibmViolations() {
      return this.result.ibm_violations || []
    },

    ibmRecommendations() {
      return this.result.ibm_recommendations || []
    },

    waveErrors() {
      return this.result.wave_errors || []
    },

    waveAlerts() {
      return this.result.wave_alerts || []
    },

    waveFeatures() {
      return this.result.wave_features || []
    },

    designContentViolations() {
      const designRules = [
        'text_contrast_sufficient', 'img_alt_valid', 'img_alt_background',
        'img_alt_null', 'img_alt_redundant', 'img_alt_decorative',
        'element_lang_valid', 'html_lang_valid', 'html_lang_exists',
        'heading_content_exists', 'text_block_heading',
        'a_text_purpose', 'style_color_misuse', 'text_sensory_misuse',
        'figure_label_exists', 'media_alt_exists', 'media_alt_brief',
        'text_spacing_valid', 'style_focus_visible',
      ]
      return this.groupViolations(
        this.ibmViolations.filter(v => designRules.includes(v.ruleId))
      )
    },

    codeStructureViolations() {
      const designRules = [
        'text_contrast_sufficient', 'img_alt_valid', 'img_alt_background',
        'img_alt_null', 'img_alt_redundant', 'img_alt_decorative',
        'element_lang_valid', 'html_lang_valid', 'html_lang_exists',
        'heading_content_exists', 'text_block_heading',
        'a_text_purpose', 'style_color_misuse', 'text_sensory_misuse',
        'figure_label_exists', 'media_alt_exists', 'media_alt_brief',
        'text_spacing_valid', 'style_focus_visible',
      ]
      return this.groupViolations(
        this.ibmViolations.filter(v => !designRules.includes(v.ruleId))
      )
    },
  },

  methods: {
    getValidSelectors(item) {
      if (!item.selectors || !Array.isArray(item.selectors)) return []
      return item.selectors.filter(s => typeof s === 'string' && s.length > 0)
    },

    simplifySelector(selector) {
      if (!selector || typeof selector !== 'string') return ''
      // Extract the meaningful parts: element type, ID, and position
      const parts = selector.split(' > ')
      const meaningful = parts
        .filter(p => !['HTML', 'BODY#juniper'].includes(p))
        .map(p => {
          // Extract tag name and ID
          const match = p.match(/^([A-Z]+)(?:#([\w-]+))?/)
          if (!match) return p
          const tag = match[1].toLowerCase()
          const id = match[2]
          if (id) return `${tag}#${id}`
          return tag
        })
      // Show last 3 meaningful parts for readability
      const last = meaningful.slice(-3)
      return (meaningful.length > 3 ? '... > ' : '') + last.join(' > ')
    },

    async loadRemediationLog() {
      if (!this.result || !this.result.audit_id) return
      try {
        const response = await api.get(`/juniper/ada-auditor/audits/${this.result.audit_id}/remediation-log`)
        if (response && response.data && response.data.success) {
          this.remediationProgress = response.data.progress
          // Key by rule_id + source + location for matching
          const log = {}
          ;(response.data.logs || []).forEach(entry => {
            // Match to this result's entries
            if (entry.audit_result_id === this.result.id) {
              const key = `${entry.issue_source}:${entry.rule_id}:${entry.element_location || ''}`
              if (!log[key]) log[key] = []
              log[key].push(entry)
            }
          })
          this.remediationLog = log
        }
      } catch (error) {
        // Remediation log not available yet
      }
    },

    getRemediationEntry(source, ruleId, location) {
      const key = `${source}:${ruleId}:${location || ''}`
      const entries = this.remediationLog[key]
      return entries ? entries[0] : null
    },

    async updateRemediation(entry, field, value) {
      if (!entry) return
      const saveKey = entry.id
      this.$set(this.savingRemediation, saveKey, true)
      try {
        await api.post(`/juniper/ada-auditor/remediation/${entry.id}/update`, { [field]: value })
        entry[field] = value
        if (field === 'status' && value !== 'open') {
          entry.resolved_at = new Date().toISOString()
        }
        // Refresh progress
        this.loadRemediationLog()
      } catch (error) {
        console.error('Failed to update remediation:', error)
      } finally {
        this.$set(this.savingRemediation, saveKey, false)
      }
    },

    statusColor(status) {
      const map = { open: 'secondary', fixed: 'success', acknowledged: 'info', wont_fix: 'warning' }
      return map[status] || 'secondary'
    },

    toggleExpanded(group) {
      this.$set(this.expandedGroups, group.ruleId, !this.expandedGroups[group.ruleId])
    },

    isExpanded(group) {
      return !!this.expandedGroups[group.ruleId]
    },

    groupViolations(violations) {
      const grouped = {}
      violations.forEach(v => {
        const key = v.ruleId || 'unknown'
        if (!grouped[key]) {
          grouped[key] = { ...v, ruleId: key, count: 1, instances: [v], expanded: false }
        } else {
          grouped[key].count++
          grouped[key].instances.push(v)
        }
      })
      return Object.values(grouped)
    },
    async openShowcase() {
      // Set loading state
      this.loadingShowcase = true

      try {
        // Pre-fetch the showcase to prepare it (and cache it)
        const token = localStorage.getItem('auth-token')
        const showcaseUrl = `${this.$store.state.apiUrl}/juniper/ada-auditor/results/${this.result.id}/showcase?token=${token}`

        // Fetch to warm up cache and check if it's accessible
        const response = await fetch(showcaseUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (!response.ok) {
          // Handle error responses
          const errorData = await response.json().catch(() => ({ message: 'Unknown error' }))
          throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`)
        }

        // Success - open in new window (will load instantly from cache)
        window.open(showcaseUrl, '_blank', 'width=1400,height=900')

      } catch (error) {
        console.error('Failed to open showcase:', error)

        // Show user-friendly error message
        let errorMessage = 'Failed to open Live Showcase'
        if (error.message) {
          errorMessage = error.message
        }

        alert(errorMessage)
      } finally {
        // Reset loading state
        this.loadingShowcase = false
      }
    }
  },

  mounted() {
    this.loadRemediationLog()

    // Auto-select first tab with data
    if (this.ibmViolations.length > 0) {
      this.activeTab = 'ibm-violations'
    } else if (this.ibmRecommendations.length > 0) {
      this.activeTab = 'ibm-recommendations'
    } else if (this.waveErrors.length > 0) {
      this.activeTab = 'wave-errors'
    } else if (this.waveAlerts.length > 0) {
      this.activeTab = 'wave-alerts'
    } else {
      this.activeTab = 'wave-features'
    }
  }
}
</script>

<style scoped>
.modal {
  display: block;
}

.text-break {
  word-break: break-word;
}
</style>
